import { assertUnreachable } from '@sgme/fp';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { WorkspaceType } from 'state/workspaces/model';
import type { IHttpClient } from 'utils/http/httpClient.model';

import {
  ApiVersion,
  type ITabsApi,
  type PersistedTabContent,
  type PersistedTabsDescription,
  type TabsApiNamespace,
} from './tabs.model';

const API = 'api/v1/tab-content';
const API_V2 = 'api/v2/tab-content';

const API_BY_VERSION: Record<ApiVersion, string> = {
  [ApiVersion.V1]: API,
  [ApiVersion.V2]: API_V2,
};
export function tabsApiConnector(http$: IHttpClient, workspace: WorkspaceType): ITabsApi {
  const namespace = getTabsApiNamespace(workspace);

  return {
    getAllTabDescriptions(): Observable<PersistedTabsDescription> {
      return http$
        .getJSON<PersistedTabsDescription>({
          url: `api/v1/tabs/${namespace}`,
        })
        .pipe(map(r => r.payload));
    },
    updateTabDescriptions(tabsDescription: PersistedTabsDescription): Observable<void> {
      return http$
        .put({
          url: `api/v1/tabs/${namespace}`,
          body: tabsDescription,
        })
        .pipe(map(r => r.payload));
    },
    clearAllTabDescriptions(): Observable<void> {
      return http$
        .delete({
          url: `api/v1/tabs/${namespace}`,
        })
        .pipe(map(r => r.payload));
    },
    getTabContent<T>(tabId: string, version: ApiVersion = ApiVersion.V1): Observable<T> {
      const baseUrl = API_BY_VERSION[version];
      const url = `${baseUrl}/${namespace}/${tabId}`;

      return http$
        .getJSON<T>({
          url,
        })
        .pipe(map(({ payload }) => payload));
    },
    updateTabContent(tabId: string, tabContent: PersistedTabContent): Observable<void> {
      return http$
        .put({
          url: `api/v1/tab-content/${namespace}/${tabId}`,
          body: tabContent,
        })
        .pipe(map(r => r.payload));
    },
    clearTabContent(tabId: string): Observable<void> {
      return http$
        .delete({
          url: `/api/v1/tab-content/${namespace}/${tabId}`,
        })
        .pipe(map(r => r.payload));
    },
  };
}
export type ITabsApiConnector = typeof tabsApiConnector;

function getTabsApiNamespace(workspace: WorkspaceType): TabsApiNamespace {
  switch (workspace) {
    case 'newBatch':
      return 'batch-screen';
    case 'static':
      return 'static-screen';
    case 'dynamic':
      return 'dynamic-screen';
    case 'ordersBasket':
      return 'orders-basket';
    case 'addonManagement':
    case 'postTrade':
      return 'post-trade';
    case 'secondaryMarketQuotes':
      return 'secondary-market-quotes';
    case 'secondaryMarketPostEvents':
      return 'secondary-market-post-events';
    case 'essential':
      return 'essential';
    case 'smartQuote':
      return 'smart-quote';
    default:
      return assertUnreachable(workspace, 'Workspace type not handled');
  }
}
